import { type AclStatusKey, getAclStatus } from "./aclStatus";

export interface AclCardInput {
  Status: number;
  DisableForUser: boolean;
  ClickToGrant: boolean;
  RequireTOTP: boolean;
  Error?: string;
  // ExpiredNeedsRenew marks a duration-limited auto-grant knoc whose allotment
  // expired this session, rendered as an enabled click-to-renew card.
  ExpiredNeedsRenew?: boolean;
}

export interface EffectiveAclCardStateInput {
  acl: AclCardInput;
  childAcl?: AclCardInput | null;
  userHasTotpEnabled: boolean;
  // True when the knoc has delegated/agentic device IPs. The badge must render
  // for those even when the owner's own session has no grant (e.g. an approved
  // agent on a click-to-grant knoc).
  hasDevices?: boolean;
}

export type CardSection =
  | "totpSetup"
  | "disabledByAdmin"
  | "agentOffline"
  | "greynoiseBlocked"
  | "staticBlocked"
  | "ipExcluded"
  | "blockedGeneric"
  | "expiredRenew"
  | "pending"
  | "clickToGrant"
  | "empty";

export interface ChildInfo {
  statusCode: number;
  statusName: string;
  isHealthy: boolean;
  error?: string;
}

export interface EffectiveAclCardState {
  disabled: boolean;
  disabledDueToTOTP: boolean;
  section: CardSection;
  buttonStatus: number;
  showStatusBadge: boolean;
  badgeStatus: number;
  badgeError: string | undefined;
  childInfo: ChildInfo | null;
  revoking: boolean;
  awaitingAgent: boolean;
}

export function getEffectiveAclCardState(input: EffectiveAclCardStateInput): EffectiveAclCardState {
  const { acl, childAcl, userHasTotpEnabled, hasDevices } = input;

  const parentStatus = getAclStatus(acl.Status as AclStatusKey);

  const disabledDueToTOTP = acl.RequireTOTP && !userHasTotpEnabled;
  const blocked = parentStatus?.blocked ?? false;
  const disabled = disabledDueToTOTP || acl.DisableForUser || (parentStatus?.agentOffline ?? false) || blocked;

  // Section priority cascade.
  //
  // Specific block reasons are resolved BEFORE the generic DisableForUser
  // branch. DisableForUser is set server-side to !IpExclusions.IpAllowed(ip),
  // the exact condition that produces an "ip_excluded" block, so checking it
  // first would shadow the dedicated ipExcluded card (and any other block
  // reason coinciding with an excluded IP) behind the vaguer "disabled" copy.
  // DisableForUser remains as the fallback for an excluded IP with no block row
  // recorded (e.g. an ungranted click-to-grant knoc).
  let section: CardSection;
  if (disabledDueToTOTP) {
    section = "totpSetup";
  } else if (blocked && acl.Error === "threatintel_static_block") {
    section = "staticBlocked";
  } else if (blocked && acl.Error === "greynoise_malicious") {
    section = "greynoiseBlocked";
  } else if (blocked && acl.Error === "ip_excluded") {
    section = "ipExcluded";
  } else if (blocked) {
    // Any other blocked reason still shows a meaningful message rather than a
    // blank disabled card.
    section = "blockedGeneric";
  } else if (acl.DisableForUser) {
    section = "disabledByAdmin";
  } else if (parentStatus?.agentOffline) {
    section = "agentOffline";
  } else if (acl.ExpiredNeedsRenew) {
    // Duration-limited auto-grant whose allotment expired this session: an
    // enabled "click to renew" card (not a block, not a plain click-to-grant).
    section = "expiredRenew";
  } else if (!acl.ClickToGrant && parentStatus?.pending) {
    section = "pending";
  } else if (acl.ClickToGrant) {
    section = "clickToGrant";
  } else {
    section = "empty";
  }

  // Badge: driven by the parent's own grant, OR by delegated/agentic devices.
  // An agent's access must show on the card even when the owner has no grant.
  const showStatusBadge =
    !disabled && ((parentStatus?.grantIssued ?? false) || (parentStatus?.error ?? false) || (hasDevices ?? false));

  // Child info (informational only, never affects card interaction)
  let childInfo: ChildInfo | null = null;
  const childStatus = childAcl != null ? getAclStatus(childAcl.Status as AclStatusKey) : undefined;
  if (childAcl != null) {
    childInfo = {
      statusCode: childAcl.Status,
      statusName: childStatus?.name ?? "Unknown",
      // Healthy = settled, issued access (Granted/Read).
      isHealthy: (childStatus?.grantIssued ?? false) && !(childStatus?.unstable ?? false),
      error: childAcl.Error,
    };
  }

  // Awaiting agent: the child's grant is still being applied.
  const awaitingAgent = (childStatus?.pending ?? false) && childAcl != null;

  // Revoking: parent or child is revoking (or failed to revoke).
  const revoking = (parentStatus?.revoking ?? false) || (childStatus?.revoking ?? false);

  return {
    disabled,
    disabledDueToTOTP,
    section,
    buttonStatus: acl.Status,
    showStatusBadge,
    badgeStatus: acl.Status,
    badgeError: acl.Error,
    childInfo,
    revoking,
    awaitingAgent,
  };
}
